import React, { useMemo } from 'react';
import { components } from 'react-select';
import classNames from 'classnames';

import type { LiveStreamEntries } from '../../types';

import { type ComponentsConfig, Control, Option } from 'app/design-system/components/Dropdown';
import { Icon } from 'app/design-system/components/Icon';
import { useTranslation } from 'app/lang';
import type { SportId } from 'app/lib/betting/constants';

import { getSportIconName, getSportName } from './sport-utils';

import dictionary from './MatchesDropdown.dictionary.json';

import styles from './MatchesDropdown.module.scss';

export type DropdownOption = {
  label: string;
  value: number;
  sportId: SportId;
  matchId:number;
  showSportTile: boolean;
  topMatch: boolean;
};

export const DropdownComponents = (
  entries: LiveStreamEntries,
  isStreamEntitiesChanged: boolean,
): ComponentsConfig<DropdownOption> => {
  const { t } = useTranslation();
  return useMemo(() => ({
    SingleValue: (props) => {
      return (
        <components.SingleValue {...props}>
          <Icon icon={getSportIconName(props.data.sportId)}
                className={styles.sportIconSingleValue}
                size={16} />
          {props.children}
        </components.SingleValue>
      );
    },
    Option: (props) => {
      const { sportId, matchId, showSportTile, topMatch } = props.data;
      const sportName = getSportName(entries, matchId, t);

      const optionClassNames = classNames(styles.option, {
        [styles.topMatch]: topMatch,
      });

      return (
        <>
          {showSportTile && (
            <div className={styles.sportInfo}>
              <Icon icon={getSportIconName(sportId)}
                    className={styles.sportIcon}
                    size={16} />
              <span className={styles.sportType}>
                {sportName}
              </span>
            </div>
          )}
          <Option {...props}
                  customInnerProps={{
                    ['data-ga-event']: 'right-broadcastSelect',
                    ['data-ga-sport-match']: matchId,
                  }}>
            <div className={optionClassNames}>
              {props.children}
            </div>
          </Option>
        </>
      );
    },
    MenuPortal: (props) => (
      <components.MenuPortal {...props}>
        <div className={styles.portal}>
          {props.children}
        </div>
      </components.MenuPortal>
    ),
    Control: (props) => (
      <Control {...props}
               customInnerProps={{
                 title: t(dictionary.selectTitle),
                 ['data-ga-event']: 'right-broadcastMenu',
               }}>
        {props.children}
      </Control>
    ),
  }), [isStreamEntitiesChanged]);
};
